<script context="module" lang="ts">
  export type sourceType = {
    title: string;
    poster: string;
    src: string;
    type: string;
    minWidth: number;
    maxWidth: number;
    invert: boolean;
  };
</script>

<script lang="ts">
  import Icon from "../Icon/Icon.svelte";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import inView from "$lib/utils/inView";
  import Video from "$lib/components/Video/Video.svelte";

  export let sources: sourceType[];
  export let autoplay = false;
  export let controls = true;
  export let muted = true;
  export let loop = false;
  export let fluid = true;
  export let preload = "auto";
  export let altText = "";

  let windowWidth: number;
  let isInView: boolean = false;
</script>

<style lang="scss">
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  .campaign-film-wrapper {
    opacity: 0;
    transition: $transition;
    padding: 32px 0;
    border-bottom: 1px solid $background;

    @include retina {
      border-bottom: 0.5px solid $background;
    }

    @include breakpoint(lg) {
      padding: 64px 3.25rem;
    }

    @include breakpoint(xl) {
      padding: 64px 4rem;
    }

    @include breakpoint(xxl) {
      padding: 64px 6rem;
    }

    &.fade-in {
      opacity: 1;
    }
  }

  .campaign-film {
    position: relative;

    :global(.vjs-big-play-button) {
      display: none;
    }

    /* Hide all controls */
    :global(.vjs-control) {
      display: none;
      text-transform: uppercase;
      font-weight: bold;
      color: $surface;
      font-size: 0.75rem;
      line-height: 1rem;
    }

    :global(.vjs-has-started.vjs-user-inactive.vjs-playing .vjs-control-bar) {
      visibility: visible;
      opacity: 1;
      transition: none;
    }

    /* Show the controls we want */
    :global(.vjs-play-control),
    :global(.vjs-progress-control),
    :global(.vjs-mute-control),
    :global(.vjs-volume-panel),
    :global(.vjs-remaining-time) {
      display: flex;
    }

    :global(.vjs-control-bar) {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      bottom: 48px;
      width: calc(100% - (44px * 2));
      left: 50%;
      transform: translateX(-50%);
      z-index: 10;
      background: transparent;
    }

    /* progress bar*/
    :global(.vjs-slider) {
      background: $inverse-on-surface-10;
    }
    /* Buffer bar */
    :global(.vjs-load-progress) {
      background: $inverse-on-surface-30;
    }

    :global(.vjs-load-progress div) {
      background: $inverse-on-surface-30;
    }

    /* Play bar */
    :global(.vjs-play-progress) {
      background: $inverse-on-surface;

      &:before {
        display: none;
      }
    }

    /* Reorder components */
    :global(.vjs-progress-control) {
      width: 100%;
      order: -2;
    }

    :global(.vjs-volume-panel) {
      order: -1;
      display: flex;
      align-items: center;

      &:after {
        content: "Sound";
      }
    }

    :global(.vjs-progress-control .vjs-progress-holder) {
      margin: 0;
    }

    :global(.vjs-mute-control) {
      width: auto;
      margin-right: 5px;
    }

    /* Prevent the volume bar nudging the play button */
    :global(.vjs-volume-panel.vjs-volume-panel-horizontal.vjs-hover) {
      width: 4em;
    }

    :global(.vjs-play-control) {
      display: flex;
      align-items: center;
      margin-left: -10px;
    }

    /* Align button and content */
    :global(.vjs-play-control.vjs-control) {
      display: flex;
      justify-content: center;
    }

    :global(.vjs-button > .vjs-icon-placeholder:before) {
      position: relative;
      left: 0;
      line-height: 1.3;
      margin-right: 5px;
    }

    :global(.vjs-remaining-time) {
      display: flex;
      align-items: center;
      padding: 0;
    }

    :global(.vjs-poster) {
      background-size: cover;
    }

    :global(.vjs-poster img) {
      object-fit: cover;
    }

    /* video title */
    .video-title {
      position: absolute;
      top: 45%;
      left: 50%;
      transform: translateX(-50%) translateY(-50%);
      z-index: 100;
      text-align: center;

      &:hover {
        cursor: pointer;
      }

      .play {
        position: relative;
        display: inline-block;

        &:after {
          content: "";
          position: absolute;
          bottom: -5px;
          left: 0;
          width: 100%;
          height: 1px;
          background: $surface;
        }
      }
    }

    /* Video fullscreen icon */
    .full-screen {
      position: absolute;
      bottom: 84px;
      right: 44px;
      z-index: 10;
      background: $surface;
      width: 32px;
      height: 32px;
      border-radius: 100%;
      color: $on-surface;
      padding: 4px;
      cursor: pointer;
    }

    @include breakpoint(md) {
      :global(.vjs-play-control:after) {
        content: "Play";
        order: 2;
      }

      :global(.vjs-has-started.vjs-user-inactive.vjs-playing .vjs-control-bar) {
        visibility: visible;
        opacity: 0;
        transition:
          visibility 1s,
          opacity 1s;
      }
    }

    &.invert {
      :global(.vjs-control) {
        color: $on-surface;
      }

      :global(.vjs-slider) {
        background: $on-surface-10;
      }
      :global(.vjs-load-progress) {
        background: $on-surface-30;
      }

      :global(.vjs-play-progress) {
        background: $on-surface;
      }

      .full-screen {
        background: $on-surface;
        color: $surface;
      }

      .video-title .play:after {
        background: $on-surface;
      }
    }
  }
</style>

<svelte:window bind:innerWidth={windowWidth} />

<div
  data-cs-override-id="film-feature"
  use:inView={{
    threshold: 0.1,
  }}
  on:intersect={({ detail }) => {
    if (detail) {
      isInView = true;
    }
  }}
>
  <div class="campaign-film-wrapper {isInView ? 'fade-in' : ''}">
    <p class="sr-only">{altText}</p>

    {#each sources as source}
      {#if windowWidth && windowWidth >= source.minWidth && windowWidth < source.maxWidth}
        <div class="campaign-film" class:invert={source.invert}>
          <Video
            poster={source.poster}
            sources={[
              {
                src: source.src,
                type: source.type ? source.type : "video/mp4",
              },
            ]}
            {autoplay}
            {controls}
            {muted}
            {loop}
            {fluid}
            {preload}
            inactivityTimeout={1}
          >
            <div slot="title">
              {#if source.title && !autoplay && controls}
                <div class="video-title">
                  <Typography
                    component="p"
                    theme={source.invert ? "on-surface" : "surface"}
                    variant="h1">{source.title}</Typography
                  >
                  <div class="play">
                    <Typography
                      component="p"
                      theme={source.invert ? "on-surface" : "surface"}
                      variant="button1">Play</Typography
                    >
                  </div>
                </div>
              {/if}
            </div>

            <div slot="fullscreen">
              {#if controls}
                <div class="full-screen">
                  <Icon
                    icon="open_in_full"
                    color={source.invert
                      ? "var(--surface)"
                      : "var(--on-surface)"}
                  />
                </div>
              {/if}
            </div>
          </Video>
        </div>
      {/if}
    {/each}
  </div>
</div>
